import React, { createContext, useContext, useState, useCallback, useMemo } from "react";
import { api } from "../services/api";
import { useNotification } from "./NotificationContext";
import { WORK_LOCATION_OPTIONS } from "../constants/workLocations";

const LocationContext = createContext();

export const useWorkLocations = () => {
  const context = useContext(LocationContext);
  if (!context) {
    throw new Error("useWorkLocations must be used within a LocationProvider");
  }
  return context;
};

export const LocationProvider = ({ children }) => {
  const [sites, setSites] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const { success, error: showError } = useNotification();

  const fetchSites = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await api.get("/sites");
      const data = response.data?.data || [];
      setSites(data);
      return data;
    } catch (err) {
      console.error("Error fetching sites:", err);
      setError("Failed to fetch locations");
      showError("Failed to load locations");
      return [];
    } finally {
      setIsLoading(false);
    }
  }, [showError]);

  const createSite = useCallback(
    async (payload) => {
      try {
        setIsLoading(true);
        const response = await api.post("/sites", payload);
        await fetchSites();
        success("Location created successfully");
        return { success: true, data: response.data?.data };
      } catch (err) {
        const message = err.response?.data?.message || "Failed to create location";
        showError(message);
        return { success: false, error: message };
      } finally {
        setIsLoading(false);
      }
    },
    [fetchSites, success, showError]
  );

  const updateSite = useCallback(
    async (id, payload) => {
      try {
        setIsLoading(true);
        const response = await api.put(`/sites/${id}`, payload);
        await fetchSites();
        success("Location updated successfully");
        return { success: true, data: response.data?.data };
      } catch (err) {
        const message = err.response?.data?.message || "Failed to update location";
        showError(message);
        return { success: false, error: message };
      } finally {
        setIsLoading(false);
      }
    },
    [fetchSites, success, showError]
  );

  const deleteSite = useCallback(
    async (id) => {
      try {
        setIsLoading(true);
        await api.delete(`/sites/${id}`);
        await fetchSites();
        success("Location deleted successfully");
        return { success: true };
      } catch (err) {
        const message = err.response?.data?.message || "Failed to delete location";
        showError(message);
        return { success: false, error: message };
      } finally {
        setIsLoading(false);
      }
    },
    [fetchSites, success, showError]
  );

  const seedDefaultSites = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await api.post("/sites/seed-defaults");
      const data = response.data?.data || [];
      setSites(data);
      success(response.data?.message || "Default locations loaded");
      return { success: true, data };
    } catch (err) {
      const message = err.response?.data?.message || "Failed to load default locations";
      showError(message);
      return { success: false, error: message };
    } finally {
      setIsLoading(false);
    }
  }, [success, showError]);

  const locationOptions = useMemo(() => {
    if (sites.length > 0) {
      return sites.map((s) => ({
        key: s.key,
        label: s.label || s.name,
        address: s.address,
        _id: s._id,
      }));
    }
    return WORK_LOCATION_OPTIONS;
  }, [sites]);

  const getWorkLocationAddress = useCallback(
    (key) => {
      const match = locationOptions.find((o) => o.key === key);
      return match?.address || "";
    },
    [locationOptions]
  );

  const inferWorkLocationKeyFromAddress = useCallback(
    (address) => {
      const text = String(address || "").toLowerCase();
      if (!text) return "";
      const match = locationOptions.find(
        (o) =>
          text.includes((o.label || "").toLowerCase()) ||
          text.includes((o.address || "").toLowerCase()) ||
          text.includes(o.key.replace(/_/g, " "))
      );
      return match?.key || "";
    },
    [locationOptions]
  );

  const value = {
    sites,
    locationOptions,
    isLoading,
    error,
    fetchSites,
    createSite,
    updateSite,
    deleteSite,
    seedDefaultSites,
    getWorkLocationAddress,
    inferWorkLocationKeyFromAddress,
  };

  return <LocationContext.Provider value={value}>{children}</LocationContext.Provider>;
};
